import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Building2, Shield, ChevronLeft, CheckCircle, DollarSign, BarChart3, Clock } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { COMPANY_INFO } from '@/lib/index';

const OPPORTUNITIES = [
  {
    icon: Building2,
    title: 'الاستثمار في مشاريع الفنادق',
    tag: 'عائد مرتفع',
    tagColor: 'bg-green-50 text-green-700 border-green-100',
    desc: 'استثمر في مشاريع الفنادق الفاخرة بالمنطقة المركزية للمدينة المنورة، وأعلى الطلب السياحي والديني على مستوى العالم.',
    details: ['عوائد تتراوح بين 12% - 18% سنوياً', 'مشاريع بالمنطقة المركزية قرب الحرم', 'ضمانات قانونية وعقود واضحة', 'فريق متخصص لإدارة المشروع'],
  },
  {
    icon: TrendingUp,
    title: 'الشراكة في مشاريع التطوير',
    tag: 'شراكة استراتيجية',
    tagColor: 'bg-blue-50 text-blue-700 border-blue-100',
    desc: 'فرصة للمشاركة كشريك مطوّر في مشاريع البناء الفندقي من مرحلة التصميم حتى التشغيل الكامل.',
    details: ['حصة ملكية حقيقية في المشروع', 'مشاركة في الأرباح التشغيلية', 'إدارة احترافية متكاملة', 'خروج مرن عند الانتهاء'],
  },
  {
    icon: Shield,
    title: 'تمويل المشاريع قصيرة الأجل',
    tag: 'أمان عالٍ',
    tagColor: 'bg-amber-50 text-amber-700 border-amber-100',
    desc: 'فرصة للتمويل المضمون بعقود مشاريع قائمة، مع عوائد ثابتة وضمانات واضحة.',
    details: ['مدة التمويل 6 - 18 شهراً', 'ضمانات عقارية وعقود مشاريع', 'عوائد ثابتة ومضمونة', 'شفافية كاملة في التقارير'],
  },
];

const PROCESS = [
  { step: '01', title: 'التواصل الأولي', desc: 'أرسل استفسارك وسيتواصل معك فريقنا الاستثماري' },
  { step: '02', title: 'دراسة الفرصة', desc: 'نقدم لك دراسة تفصيلية للفرصة الاستثمارية والعوائد المتوقعة' },
  { step: '03', title: 'التفاوض والاتفاق', desc: 'نتفق على الشروط والهيكل الاستثماري المناسب' },
  { step: '04', title: 'التوثيق القانوني', desc: 'توقيع العقود وتوثيقها بصورة نظامية ومضمونة' },
  { step: '05', title: 'البدء والتنفيذ', desc: 'انطلاق المشروع مع تقارير دورية منتظمة' },
];

export default function Investment() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', type: '', amount: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: '', phone: '', email: '', type: '', amount: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/luxury_hotel_interior_8.jpeg" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-navy/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/30 rounded-full text-gold text-sm mb-6">
              <TrendingUp size={14} /><span>فرص التمويل والاستثمار</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              استثمر في <span className="text-gold">مستقبل المدينة المنورة</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed">
              فرص استثمارية حقيقية في قطاع الفنادق الفاخرة بأعلى الطلب وأكثر المواقع قيمةً في العالم
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
              {[{ n: '12-18%', l: 'عائد سنوي متوقع' }, { n: '50+', l: 'مشروع ناجح' }, { n: '100%', l: 'شفافية في التقارير' }].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-black text-gold" style={{ fontFamily: 'Tajawal,sans-serif' }}>{s.n}</div>
                  <div className="text-white/70 text-sm">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              الفرص <span className="text-gold">الاستثمارية</span> المتاحة
            </h2>
            <p className="text-muted-foreground">فرص مدروسة بعوائد حقيقية في سوق نموٍّ مستمر</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {OPPORTUNITIES.map(({ icon: Icon, title, tag, tagColor, desc, details }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }} viewport={{ once: true }}
                className="bg-white border border-border rounded-2xl p-6 hover:border-navy/30 hover:shadow-xl transition-all duration-300 group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-13 h-13 bg-navy/8 rounded-xl flex items-center justify-center group-hover:bg-navy transition-colors duration-300">
                    <Icon size={24} className="text-navy group-hover:text-white transition-colors" />
                  </div>
                  <span className={`px-2.5 py-1 text-xs rounded-full font-semibold border ${tagColor}`}>{tag}</span>
                </div>
                <h3 className="text-navy font-black text-lg mb-3 group-hover:text-gold transition-colors">{title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{desc}</p>
                <ul className="space-y-2">
                  {details.map((d, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle size={14} className="text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{d}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Madinah */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-black text-navy mb-6" style={{ fontFamily: 'Tajawal,sans-serif' }}>
                لماذا <span className="text-gold">المدينة المنورة</span>؟
              </h2>
              <div className="space-y-4">
                {[
                  { Icon: BarChart3, t: 'طلب متزايد على مدار السنة', d: 'ملايين الحجاج والمعتمرين يزورون المدينة سنوياً بشكل مستمر' },
                  { Icon: TrendingUp, t: 'نمو عقاري متسارع', d: 'مشاريع رؤية 2030 ترفع الطلب على الإقامة الفاخرة بشكل كبير' },
                  { Icon: Shield, t: 'استثمار آمن ومضمون', d: 'قطاع الضيافة الديني الأكثر استقراراً واستدامة في العالم' },
                  { Icon: DollarSign, t: 'عوائد تنافسية', d: 'معدلات إشغال مرتفعة تعني عوائد متميزة مقارنة بالقطاعات الأخرى' },
                ].map(({ Icon, t, d }, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white border border-border rounded-xl p-4">
                    <div className="w-10 h-10 bg-gold/12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-gold" />
                    </div>
                    <div>
                      <h4 className="text-navy font-bold text-sm mb-1">{t}</h4>
                      <p className="text-muted-foreground text-xs leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/luxury_hotel_construction_madinah_skyscr_5.jpeg" alt="المدينة المنورة" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              كيف <span className="text-gold">تبدأ؟</span>
            </h2>
            <p className="text-muted-foreground">خطوات بسيطة وواضحة للانضمام إلى فرصتك الاستثمارية</p>
          </div>
          <div className="space-y-4">
            {PROCESS.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                className="flex items-start gap-5 bg-surface border border-border rounded-2xl p-5">
                <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-gold font-black text-sm">{p.step}</span>
                </div>
                <div className="flex-1">
                  <h4 className="text-navy font-bold mb-1">{p.title}</h4>
                  <p className="text-muted-foreground text-sm">{p.desc}</p>
                </div>
                {i < PROCESS.length - 1 && (
                  <Clock size={16} className="text-gold/40 mt-1 flex-shrink-0" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-20 bg-surface">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              أرسل <span className="text-gold">استفسارك</span>
            </h2>
            <p className="text-muted-foreground">سيتواصل معك فريقنا الاستثماري خلال 24 ساعة</p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <AnimatePresence>
              {sent && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                  ✅ تم إرسال استفسارك! سيتواصل معك فريقنا الاستثماري قريباً.
                </motion.div>
              )}
            </AnimatePresence>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الاسم الكامل *</label>
                  <input required value={form.name} onChange={e => set('name', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الجوال *</label>
                  <input required type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} dir="ltr"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">البريد الإلكتروني *</label>
                <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} dir="ltr"
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">نوع الاستثمار</label>
                  <select value={form.type} onChange={e => set('type', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر</option>
                    {OPPORTUNITIES.map(o => <option key={o.title}>{o.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">حجم الاستثمار المتوقع</label>
                  <select value={form.amount} onChange={e => set('amount', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر</option>
                    <option>أقل من 1 مليون ريال</option><option>1 - 5 مليون ريال</option><option>5 - 20 مليون ريال</option><option>أكثر من 20 مليون ريال</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">تفاصيل إضافية</label>
                <textarea rows={3} value={form.message} onChange={e => set('message', e.target.value)} placeholder="أي تفاصيل أو أسئلة تودّ الاستفسار عنها..."
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all resize-none" />
              </div>
              <button type="submit"
                className="w-full py-4 bg-navy text-white rounded-xl font-bold text-base hover:bg-navy-light transition-all hover:shadow-lg flex items-center justify-center gap-2">
                إرسال الاستفسار <ChevronLeft size={18} />
              </button>
              <p className="text-center text-xs text-muted-foreground">
                للتواصل المباشر: <a href={`tel:${COMPANY_INFO.phone}`} className="text-gold font-semibold" dir="ltr">{COMPANY_INFO.phone}</a>
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
